import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _isNil from 'lodash/isNil';
import _omitBy from 'lodash/omitBy';
import querystring from 'querystring';
import { zillowURL } from '@zillow/url';
import { isWebview } from '@zillow/mortgage-webview-query-params';
import { isUSAA, USAAUpsellURL } from '@zillow/mortgage-cobranding';

var ZHL_BASE_URL_PROD = 'https://www.zillowhomeloans.com';
var ZHL_BASE_URL_DEV = 'https://www.dev.zillowhomeloans.net';
var ZGUID_QUERY_PARAM_KEY = 'zga_z_guid';
var ZUID_QUERY_PARAM_KEY = 'zga_z_uid';

var Cookies = {
  ANALYTICS_COOKIE: 'zjs_anonymous_id',
  ENCODED_ZUID: 'zjs_user_id',
  ZGUID: 'zguid'
};

/**
 * Parse a cookie string into a dictionary of cookie key value pairs
 * @param cookies {string}
 * @returns {object}
 */
var parseCookies = function parseCookies(cookies) {
  return cookies === null || cookies === void 0 ? void 0 : cookies.split(';').map(function (cookie) {
    return cookie.split('=').map(function (cookieParts) {
      return cookieParts.trim();
    });
  }).filter(function (cookieParts) {
    return cookieParts.length === 2;
  }).reduce(function (cookieMap, _ref) {
    var _Object$assign;
    var _ref2 = _slicedToArray(_ref, 2),
      cookieKey = _ref2[0],
      cookieVal = _ref2[1];
    return Object.assign(cookieMap, (_Object$assign = {}, _Object$assign[cookieKey.toLowerCase()] = decodeURIComponent(cookieVal), _Object$assign));
  }, {});
};
var cookieObjectToZGUID = function cookieObjectToZGUID(cookies) {
  if (cookies === void 0) {
    cookies = {};
  }
  // First rely on the ZG Analytics SDK cookie value
  var sdkZGUID = cookies[Cookies.ANALYTICS_COOKIE];
  if (sdkZGUID) {
    return sdkZGUID.toLowerCase().replace(/[^a-z,0-9,-]/g, ''); // Remove all non-uuid characters
  }

  // If zjs_anonymous_id does not exist for some reason, we can try to use traditional zguid
  var baseZGUID = cookies[Cookies.ZGUID];
  if (!baseZGUID) {
    return;
  }
  var cookieStringSelectorIndex = baseZGUID.indexOf('$'); // Actual zguid value is prefixed with $
  if (cookieStringSelectorIndex < 0) {
    return;
  }
  return baseZGUID.substring(cookieStringSelectorIndex + 1);
};
var cookiesToZGUID = function cookiesToZGUID(cookies) {
  var cookieObj = parseCookies(cookies);
  return cookieObjectToZGUID(cookieObj);
};
var cookieObjectToZUID = function cookieObjectToZUID(cookies) {
  if (cookies === void 0) {
    cookies = {};
  }
  // This cookie value is maintained by the clickstream team
  var encodedZUID = cookies[Cookies.ENCODED_ZUID];
  if (encodedZUID) {
    return encodedZUID.replace(/[^A-Z,a-z,0-9,\-,_]/g, ''); // Remove all non-zuid characters
  }
};
var cookiesToZUID = function cookiesToZUID(cookies) {
  var cookieObj = parseCookies(cookies);
  return cookieObjectToZUID(cookieObj);
};

var PREQUALIFY_PATH = '/pre-qualify/';
var HOMELOANS_ELIGIBILIY_PATH = '/homeloans/eligibility/';
function propertyTypeFromGraphQL(homeType) {
  var zmmType;
  switch (homeType) {
    case 'MANUFACTURED':
      zmmType = 'MobileOrManufactured';
      break;
    case 'Townhouse':
    case 'TOWNHOUSE':
      zmmType = 'TownHouse';
      break;
    case 'MULTI_FAMILY':
    case 'CONDO':
    case 'APARTMENT':
      zmmType = 'CondoFourOrFewerStories';
      break;
    case 'SINGLE_FAMILY':
    default:
      zmmType = 'SingleFamilyHome';
      break;
  }
  return zmmType;
}
var getLongFormPath = function getLongFormPath(landing, hash) {
  if (landing === void 0) {
    landing = 'mortgages';
  }
  if (hash === void 0) {
    hash = {};
  }
  var baseUrl = landing === 'buyer-advantage' ? 'mortgages' : landing;
  var url = isWebview() ? 'mortgage/webviews/ZLF.htm' : baseUrl + "/";
  var landingPage = landing === 'mortgages' ? 'landing' : baseUrl.includes('pre-qualify') ? 'pre-qualify' : baseUrl.includes('pre-approval') ? 'pre-approval' : landing;
  var hashString = querystring.stringify(_omitBy(hash, _isNil));
  return url + "#/" + landingPage + (hashString ? "&" + hashString : '');
};
var getLongFormURL = function getLongFormURL(landing, hash, brand) {
  if (brand === void 0) {
    brand = 'Zillow';
  }
  var URI = getLongFormPath(landing, hash);
  return brand.toLowerCase() === 'trulia' && !isWebview() ? "https://www.trulia.com/mortgages/" + URI : zillowURL(URI);
};
var appendZhlParams = function appendZhlParams(document) {
  var domain = new URL(document.URL);
  var uid = cookiesToZUID(document.cookie);
  var guid = cookiesToZGUID(document.cookie);
  var query = 'www.dev.zillowhomeloans.net';
  switch (domain.hostname) {
    case 'www.zillow.com':
      query = 'www.zillowhomeloans.com';
      break;
    case 'www.qa.zillow.net':
      query = 'www.staging.zillowhomeloans.net';
      break;
  }
  var zhlLinks = document.querySelectorAll("a[href^=\"https://" + query + "\"]");
  zhlLinks.forEach(function (zhlLink) {
    var url = new URL(zhlLink.href);
    if (uid !== null) {
      url.searchParams.append('zga_z_uid', uid);
    }
    if (guid !== null) {
      url.searchParams.append('zga_z_guid', guid);
    }
    zhlLink.href = url.href;
  });
};

/**
 * Return the full zhl url with the domain, path and query params.
 * @param baseURL {string} The base URL to use.
 * @param cookie {object} A dictionary of cookies containing the zguid and zuid.
 *  If the cookie is invalid, empty, or null, the zguid and zuid won't be added to the query params.
 * @param path {string} The path to append
 * @param params Query parameters to append. This can override the cookie query params.
 * @returns {string}
 */
var buildZHLURL = function buildZHLURL(_ref) {
  var baseURL = _ref.baseURL,
    cookies = _ref.cookies,
    _ref$path = _ref.path,
    path = _ref$path === void 0 ? '/' : _ref$path,
    _ref$params = _ref.params,
    params = _ref$params === void 0 ? {} : _ref$params;
  var url = new URL(path, baseURL);
  var zguid = cookieObjectToZGUID(cookies);
  if (zguid) {
    url.searchParams.set(ZGUID_QUERY_PARAM_KEY, zguid);
  }
  var zuid = cookieObjectToZUID(cookies);
  if (zuid) {
    url.searchParams.set(ZUID_QUERY_PARAM_KEY, zuid);
  }
  Object.entries(_omitBy(params, _isNil)).forEach(function (_ref2) {
    var _ref3 = _slicedToArray(_ref2, 2),
      k = _ref3[0],
      v = _ref3[1];
    return url.searchParams.set(k, v);
  });
  return url.href;
};

/**
 * Return the full zhl url with the domain, path and query params.
 * @param baseURL {string} The base URL to use.
 * @param cookie {object} A dictionary of cookies containing the zguid and zuid.
 *  If the cookie is invalid, empty, or null, the zguid and zuid won't be added to the query params.
 * @param params Query parameters to append. This can override the cookie query params.
 * @returns {string}
 */
var buildZHLURLPrequal = function buildZHLURLPrequal(_ref4) {
  var baseURL = _ref4.baseURL,
    cookies = _ref4.cookies,
    params = _ref4.params;
  return buildZHLURL({
    baseURL: baseURL,
    cookies: cookies,
    path: HOMELOANS_ELIGIBILIY_PATH,
    params: params
  });
};
var getZHLURL = function getZHLURL(path, params) {
  var _window, _window$location, _window2, _window2$document;
  if (path === void 0) {
    path = '/';
  }
  if (params === void 0) {
    params = {};
  }
  if (typeof window === 'undefined') {
    return;
  }
  //since all qa environments under zillow.net
  var baseURL = ((_window = window) === null || _window === void 0 ? void 0 : (_window$location = _window.location) === null || _window$location === void 0 ? void 0 : _window$location.hostname.indexOf('zillow.com')) !== -1 ? ZHL_BASE_URL_PROD : ZHL_BASE_URL_DEV;
  var cookies = parseCookies((_window2 = window) === null || _window2 === void 0 ? void 0 : (_window2$document = _window2.document) === null || _window2$document === void 0 ? void 0 : _window2$document.cookie);
  return buildZHLURL({
    baseURL: baseURL,
    cookies: cookies,
    path: path,
    params: params
  });
};
var getZHLURLHome = function getZHLURLHome(params) {
  return getZHLURL('/', params);
};
var getZHLURLPrequal = function getZHLURLPrequal(params) {
  return getZHLURL(PREQUALIFY_PATH, params);
};
function getCustomQuotesURL(base, params, brand) {
  if (base === void 0) {
    base = 'mortgage-rates';
  }
  if (params === void 0) {
    params = {};
  }
  if (brand === void 0) {
    brand = 'Zillow';
  }
  var url = isWebview() ? 'mortgage/webviews/ZCQ.htm' : base + '/';
  var queryString = querystring.stringify(_omitBy(params, _isNil));
  if (queryString) {
    queryString = "?" + queryString;
  }
  var URI = url + queryString;
  return brand.toLowerCase() === 'trulia' && !isWebview() ? 'https://www.trulia.com/' + URI : zillowURL(URI);
}
var getUpsellText = function getUpsellText(loanType) {
  return loanType === 'purchase' ? 'Get pre-qualified' : 'Refinance your loan';
};
var getUpsellLink = function getUpsellLink(loanType, zipCode, propertyValue, propertyType, source, additionalParams) {
  if (source === void 0) {
    source = 'Z_ForsaleHDP_Getpre-approved';
  }
  /// if changing logic here, change at 'packages/hdp-payment-calculator-summary-wow/src/components/Upsell.jsx' as well
  if (isUSAA()) {
    return USAAUpsellURL;
  }
  if (loanType === 'purchase') {
    return getLongFormURL('mortgages/pre-qualify', Object.assign({
      zipCode: zipCode,
      propertyValue: propertyValue,
      propertyType: propertyType,
      source: source
    }, additionalParams));
  } else {
    return getCustomQuotesURL('refinance', Object.assign({
      auto: true,
      zip: zipCode,
      value: propertyValue,
      loantype: 'refinance'
    }, additionalParams));
  }
};

export { ZHL_BASE_URL_DEV, ZHL_BASE_URL_PROD, appendZhlParams, buildZHLURLPrequal, cookiesToZGUID, cookiesToZUID, getCustomQuotesURL, getLongFormPath, getLongFormURL, getUpsellLink, getUpsellText, getZHLURL, getZHLURLHome, getZHLURLPrequal, propertyTypeFromGraphQL };
